import { useState } from 'react';
import { apiCall, ApiError } from '../utils/apiHelpers';

type Category = 'bug' | 'feature-request' | 'question';

interface CommentLite {
    id: string;
    text: string;
}

interface Props {
    repoId: string;
    comment: CommentLite;
    onClose: () => void;
    onFiled: () => void;
}

interface FileResponse {
    issueNumber: number;
    issueUrl: string;
    deduplicated?: boolean;
    truncated?: boolean;
}

export function FileToGithubModal({ repoId, comment, onClose, onFiled }: Props) {
    const [category, setCategory] = useState<Category>('question');
    const [title, setTitle] = useState(comment.text.split('\n')[0].slice(0, 80));
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<FileResponse | null>(null);

    async function submit() {
        setSubmitting(true);
        setError(null);
        try {
            const [owner, name] = repoId.split('/');
            const r = await apiCall<FileResponse>(
                `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/comments/${comment.id}/file-to-github`,
                { method: 'POST', body: { category, title } }
            );
            setResult(r);
            onFiled();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="modal-overlay" onClick={onClose} role="presentation">
            <div className="modal-content" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <header className="modal-header">
                    <h2>Send to GitHub</h2>
                    <button className="btn btn--ghost" onClick={onClose} aria-label="Close">×</button>
                </header>
                {!result ? (
                    <div className="modal-body">
                        <p className="form-hint">
                            This files your comment as a GitHub issue under <code>bacr-docreview[bot]</code>. The issue body
                            records your identity and links back to the source file.
                        </p>
                        <label className="form-group">
                            <span className="form-label">Category</span>
                            <select value={category} onChange={(e) => setCategory(e.target.value as Category)}>
                                <option value="bug">Bug</option>
                                <option value="feature-request">Feature request</option>
                                <option value="question">Question</option>
                            </select>
                        </label>
                        <label className="form-group">
                            <span className="form-label">Issue title</span>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                maxLength={120}
                            />
                            <span className="form-hint">First line of your comment, editable.</span>
                        </label>
                        {error && <div className="form-error">{error}</div>}
                        <footer className="modal-footer">
                            <button className="btn btn--ghost" onClick={onClose} disabled={submitting} title="Keep this in docreview only — you can file to GitHub later from the comment's menu">
                                Save as comment only
                            </button>
                            <button className="btn btn--primary" onClick={submit} disabled={submitting}>
                                {submitting ? 'Filing…' : 'File issue on GitHub'}
                            </button>
                        </footer>
                    </div>
                ) : (
                    <div className="modal-body">
                        <div className="form-success">
                            Filed as <a href={result.issueUrl} target="_blank" rel="noreferrer noopener">#{result.issueNumber}</a>.
                            {result.truncated && <p>Body was truncated; overflow posted as a follow-up comment on the issue.</p>}
                        </div>
                        <footer className="modal-footer">
                            <button className="btn btn--primary" onClick={onClose}>Close</button>
                        </footer>
                    </div>
                )}
            </div>
        </div>
    );
}
